import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import {
    Mail,
    MapPin,
    MessageCircle,
    Instagram,
    Facebook,
    Twitter,
    Video,
    Youtube,
} from "lucide-react";

import { localPhoneNumberReadable, phoneNumberNoPlus, phoneNumberReadable } from "@src/config";
import {
    appFacebook,
    appInstagram,
    appX,
    appTikTok,
    appYoutube,
    appAddress,
    appGoogleMapsUrl,
} from "@src/app-details";
import { greenUnisonBrand } from "@src/brand/greenUnisonBrand";

import { BrandLogo } from "@components/brand/BrandLogo";
import WhatsAppLink from "@components/utils/WhatsAppLink";
import FloatingWhatsApp from "@components/ui/FloatingWhatsApp";

const smBtnClass =
    "rounded-full bg-white/10 p-1.5 transition-colors duration-300 hover:bg-brand-yellow hover:text-brand-forest sm:p-2";

const catLinksCLass = "text-brand-cream/80 transition-colors hover:text-brand-yellow";

const Footer: React.FC = () => {
    const [currentYear, setCurrentYear] = useState<string>("");

    useEffect(() => {
        setCurrentYear(String(new Date().getFullYear()));
    }, []);

    const isNigeria = true; // In a real app, this would be determined by geolocation or user preference
    const phoneNumber = isNigeria ? localPhoneNumberReadable : phoneNumberReadable;

    return (
        <footer className="bg-brand-forest text-white pt-16">
            <div className="mx-auto px-4 container">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    <div>
                        <div className="mb-6">
                            <BrandLogo dark subLabel="Limited" />
                        </div>
                        <p className="mb-6 text-brand-cream/90">
                            {greenUnisonBrand.taglines.footer}
                        </p>
                        <div className="flex flex-wrap gap-3 sm:gap-4">
                            <a
                                href={appFacebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={smBtnClass}
                                aria-label="Follow Us on Facebook"
                            >
                                <Facebook className="size-4 sm:size-5" />
                            </a>
                            <a
                                href={appInstagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={smBtnClass}
                                aria-label="Follow Us on Instagram"
                            >
                                <Instagram className="size-4 sm:size-5" />
                            </a>
                            <a
                                href={appX}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={smBtnClass}
                                aria-label="Follow Us on X"
                            >
                                <Twitter className="size-4 sm:size-5" />
                            </a>
                            <a
                                href={appTikTok}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={smBtnClass}
                                aria-label="Follow Us on Tiktok"
                            >
                                <Video className="size-4 sm:size-5" />
                            </a>
                            <a
                                href={appYoutube}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={smBtnClass}
                                aria-label="Follow Us on YouTube"
                            >
                                <Youtube className="size-4 sm:size-5" />
                            </a>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-6">Shop</h3>
                        <ul className="space-y-3">
                            <li>
                                <Link
                                    to="/shop/flours"
                                    className={catLinksCLass}
                                    aria-label="Browse Our Flours Products"
                                >
                                    Flours
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/shop/dried-vegetables"
                                    className={catLinksCLass}
                                    aria-label="Browse Our Dried Vegetable Products"
                                >
                                    Dried Vegetables
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/shop/dried-grains"
                                    className={catLinksCLass}
                                    aria-label="Browse Our Dried Grains Products"
                                >
                                    Dried Grains
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/shop/seeds"
                                    className={catLinksCLass}
                                    aria-label="Browse Our Plant Seeds Products"
                                >
                                    Plant Seeds
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-6">Company</h3>
                        <ul className="space-y-3">
                            <li>
                                <Link
                                    to="/about"
                                    className={catLinksCLass}
                                    aria-label="Learn Our Story"
                                >
                                    Our Story
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/about#sustainability"
                                    className={catLinksCLass}
                                    aria-label="Learn About Our Sustainable Practices"
                                >
                                    Sustainability
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className={catLinksCLass}
                                    aria-label="Contact Us"
                                >
                                    Contact Us
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/faq"
                                    className={catLinksCLass}
                                    aria-label="Read through Our Frequently Asked Questions"
                                >
                                    FAQs
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/shipping"
                                    className={catLinksCLass}
                                    aria-label="Learn About Our Shipping & Return Policy"
                                >
                                    Shipping & Returns
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/sitemap"
                                    className={catLinksCLass}
                                    aria-label="Learn About Our Shipping & Return Policy"
                                >
                                    Sitemap
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-6">Contact</h3>
                        <ul className="space-y-4">
                            <li className="flex">
                                <MapPin className="size-5 text-[#34d399] mr-3 shrink-0" />
                                <span className="text-[#d4d4d4]">
                                    <a
                                        href={appGoogleMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:text-white transition-colors"
                                    >
                                        {appAddress}
                                    </a>
                                </span>
                            </li>
                            <li className="flex">
                                <MessageCircle className="size-5 text-[#34d399] mr-3 shrink-0" />
                                <div className="flex flex-col">
                                    <a
                                        href={`tel:${phoneNumberNoPlus}`}
                                        rel="nofollow"
                                        className={catLinksCLass}
                                        aria-label="Call Us"
                                    >
                                        {phoneNumber}
                                    </a>
                                    <WhatsAppLink
                                        className="text-sm text-[#34d399] hover:text-[#6ee7b7] transition-colors mt-1"
                                        useLocalPhoneNumber={true}
                                    >
                                        {" "}
                                        Chat us on WhatsApp
                                    </WhatsAppLink>
                                </div>
                            </li>
                            <li className="flex">
                                <Mail className="size-5 text-[#34d399] mr-3 shrink-0" />
                                <a
                                    href="mailto:mail@greenunison.com"
                                    rel="nofollow"
                                    className={catLinksCLass}
                                    aria-label="Send us an email"
                                >
                                    mail@greenunison.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="py-6 border-t border-[#065f46] flex flex-col md:flex-row justify-between items-center text-sm text-[#9ca3af]">
                    <p>© {currentYear} Green Unison Limited. All rights reserved.</p>
                    <div className="flex gap-6 mt-4 md:mt-0">
                        <Link
                            to="/privacy-policy"
                            className="hover:text-white transition-colors"
                            aria-label="Read through Our Privacy Policy"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            to="/terms"
                            className="hover:text-white transition-colors"
                            aria-label="Read through Our Terms & Conditions"
                        >
                            Terms & Conditions
                        </Link>
                    </div>
                </div>
            </div>

            <FloatingWhatsApp />
        </footer>
    );
};

export default Footer;
